/**
 * R-131-EPISTEMIC-GROUNDING-E — operator pre-attestation on ProjectIntent.
 *
 * Converts `projectIntent.attestations` into FindingClaim rows so reports
 * never hallucinate absences the operator has already confirmed.
 */
import type { FindingClaim } from './findingClaim';
import { formatOperatorAttestationExcerpt } from './findingClaim';
import type { AttestationState, ProjectIntent, ProjectIntentAttestations } from '../types/siteRag';

/** Canonical attestation keys with stable copy for prompts and findings. */
export const PROJECT_INTENT_ATTESTATION_DEFS = [
  {
    key: 'ppaSigned',
    label: 'PPA signed',
    absenceClaim: 'Power purchase agreement (PPA) has not been executed.',
    openQuestion: 'Has the power purchase agreement (PPA) been executed?',
    enumValues: ['yes', 'no', 'in-discussion', 'unknown'] as const,
  },
  {
    key: 'speFormed',
    label: 'SPE formed',
    absenceClaim: 'Special-purpose entity (SPE) has not been formed.',
    openQuestion: 'Has the special-purpose entity (SPE) been formed?',
    enumValues: ['yes', 'no', 'in-progress', 'unknown'] as const,
  },
  {
    key: 'epcSigned',
    label: 'EPC contract signed',
    absenceClaim: 'EPC contract has not been executed.',
    openQuestion: 'Has the EPC contract been executed?',
    enumValues: ['yes', 'no', 'in-discussion', 'unknown'] as const,
  },
  {
    key: 'gasSupplyFirm',
    label: 'Firm gas supply',
    absenceClaim: 'No firm gas supply agreement is in place.',
    openQuestion: 'Is a firm gas supply agreement in place?',
    enumValues: ['yes', 'no', 'in-discussion', 'unknown'] as const,
  },
  {
    key: 'slotReservationBinding',
    label: 'Binding slot reservation',
    absenceClaim: 'Turbine / equipment slot reservation is not yet binding.',
    openQuestion: 'Is the turbine or equipment slot reservation binding?',
    enumValues: ['yes', 'no', 'in-discussion', 'unknown'] as const,
  },
] as const;

export type KnownAttestationKey = (typeof PROJECT_INTENT_ATTESTATION_DEFS)[number]['key'];

const KNOWN_KEYS = new Set<string>(PROJECT_INTENT_ATTESTATION_DEFS.map(d => d.key));

export function isKnownAttestationKey(key: string): key is KnownAttestationKey {
  return KNOWN_KEYS.has(key);
}

export function normalizeAttestationState(raw: unknown): AttestationState | undefined {
  if (typeof raw !== 'string') return undefined;
  const s = raw.trim() as AttestationState;
  const allowed: AttestationState[] = ['yes', 'no', 'in-discussion', 'in-progress', 'unknown'];
  return allowed.includes(s) ? s : undefined;
}

export function readAttestationValue(
  attestations: ProjectIntentAttestations | undefined,
  key: string,
): AttestationState | undefined {
  if (!attestations) return undefined;
  return normalizeAttestationState(attestations[key]);
}

function defForKey(key: string) {
  return PROJECT_INTENT_ATTESTATION_DEFS.find(d => d.key === key);
}

/**
 * Map one attestation field to a FindingClaim (confirmed absence or open question).
 */
export function attestationToFindingClaim(
  key: string,
  state: AttestationState | undefined,
  attestedAtIso: string,
): FindingClaim | null {
  const def = defForKey(key);
  const label = def?.label ?? key;
  const absenceClaim = def?.absenceClaim ?? `${label} is not in place.`;
  const openQuestion = def?.openQuestion ?? `Confirm status of ${label.toLowerCase()}.`;

  if (state === 'no') {
    const date = attestedAtIso.slice(0, 10);
    return {
      claim: `${absenceClaim} (operator attested ${date}).`,
      evidenceState: 'confirmed',
      evidence: {
        refIds: [],
        excerpt: formatOperatorAttestationExcerpt(attestedAtIso),
      },
    };
  }

  if (state === 'unknown' || state === undefined) {
    return {
      claim: openQuestion,
      evidenceState: 'unknown',
      openQuestion,
    };
  }

  // yes / in-discussion / in-progress — not asserted absences; LLM handles separately.
  return null;
}

/** All attestation-driven findings for known + custom keys on intent. */
export function buildProjectIntentAttestationFindings(
  intent: ProjectIntent | undefined | null,
  attestedAtIso?: string,
): FindingClaim[] {
  if (!intent?.attestations) return [];
  const at = attestedAtIso ?? new Date().toISOString();
  const keys = [
    ...PROJECT_INTENT_ATTESTATION_DEFS.map(d => d.key),
    ...Object.keys(intent.attestations).filter(k => !KNOWN_KEYS.has(k)),
  ];
  const seen = new Set<string>();
  const out: FindingClaim[] = [];
  for (const key of keys) {
    if (seen.has(key)) continue;
    seen.add(key);
    const state = readAttestationValue(intent.attestations, key);
    if (state === undefined && !Object.prototype.hasOwnProperty.call(intent.attestations, key)) {
      continue;
    }
    const claim = attestationToFindingClaim(key, state, at);
    if (claim) out.push(claim);
  }
  return out;
}

function claimDedupeKey(c: FindingClaim): string {
  return (c.openQuestion ?? c.claim).trim().toLowerCase();
}

/** Merge operator-attested findings into an existing FindingClaim list (no duplicates). */
export function mergeAttestationFindings(
  existing: FindingClaim[],
  intent: ProjectIntent | undefined | null,
  attestedAtIso?: string,
): FindingClaim[] {
  const attested = buildProjectIntentAttestationFindings(intent, attestedAtIso);
  if (attested.length === 0) return existing;
  const seen = new Set(existing.map(claimDedupeKey));
  const merged = [...existing];
  for (const c of attested) {
    const k = claimDedupeKey(c);
    if (seen.has(k)) continue;
    seen.add(k);
    merged.push(c);
  }
  return merged;
}

/** Prompt block — operator attestations are authoritative for absence claims. */
export function buildProjectIntentAttestationsBlock(
  intent: ProjectIntent | undefined | null,
): string {
  if (!intent?.attestations) return '';
  const lines: string[] = [];
  for (const { key, label } of PROJECT_INTENT_ATTESTATION_DEFS) {
    const state = readAttestationValue(intent.attestations, key);
    if (!state) continue;
    lines.push(`  • ${label}: ${state}`);
  }
  for (const [key, raw] of Object.entries(intent.attestations)) {
    if (KNOWN_KEYS.has(key)) continue;
    const state = normalizeAttestationState(raw);
    if (!state) continue;
    lines.push(`  • ${key}: ${state}`);
  }
  if (lines.length === 0) return '';

  return [
    '═══ OPERATOR ATTESTATIONS (PROJECT INTENT — AUTHORITATIVE) ═══',
    '',
    'The site operator has pre-attested the following factual states. When an attestation is "no",',
    'you MAY report the absence as a confirmed red flag citing operator attestation (no document [Ref] required).',
    'When an attestation is "unknown" or unset, emit an open question — never assert absence.',
    '',
    ...lines,
    '',
    '═══════════════════════════════════════════════════════════════',
    '',
  ].join('\n');
}

/** Diff attestations for audit-log emission. */
export function diffProjectIntentAttestations(
  before: ProjectIntent | undefined | null,
  after: ProjectIntent | undefined | null,
): Array<{ key: string; from?: AttestationState; to?: AttestationState }> {
  const prev = before?.attestations ?? {};
  const next = after?.attestations ?? {};
  const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
  const changes: Array<{ key: string; from?: AttestationState; to?: AttestationState }> = [];
  for (const key of keys) {
    const from = normalizeAttestationState(prev[key]);
    const to = normalizeAttestationState(next[key]);
    if (from === to) continue;
    changes.push({ key, from, to });
  }
  return changes;
}
